import baseAxios from 'axios';
import axios from '../../utils/axios';
import { objectToFormData } from '../utils';
import { formatLocalDateAsUtcMidnight } from '../../utils/formatTime';

// GET
// /Api/ConnectedItems

// GET
// /Api/OrdersUnread
export async function getOrdersUnreadData() {
  const response = await axios.get('/OrdersUnread');
  return response;
}

// GET
// /Api/ReplacementData orderId
export async function getReplacementData(orderId) {
  const response = await axios.get(`/ReplacementData?orderId=${orderId}`);
  return response;
}

// POST
// /Api/UpdateOrderGuidePrices facilityId items {  "itemId": 0,  "price": 0}
export async function postUpdateOrderGuidePrices({ facilityId, items }) {
  const formData = objectToFormData({
    facilityId,
    items: items?.map((item) => ({
      itemId: item.itemId,
      price: item.updatedPrice,
    })),
  });
  const response = await axios.post('/UpdateOrderGuidePrices', formData);
  return response;
}

// POST
// /Api/ApproveOrder

// POST
// /Api/CombineOrders
export async function postCombineOrder(orderIdArray) {
  const originalOrderId = orderIdArray[0];
  const combineOrders = orderIdArray?.slice(1);
  const formData = objectToFormData({ originalOrderId, combineOrders });
  const response = await axios.post('/CombineOrders', formData);
  return response;
}

// POST
// /Api/SplitOrder orderId items
export async function postSplitOrder({ orderId, selectedRows, data }) {
  const itemsToPost = data?.map((item) => ({
    id: item.id,
    vendorId: item.vendorId,
    quantity: item.quantity,
    uomAmount: item.uomAmount,
    itemId: item.itemId,
    agoraCategoryId: item.agoraCategory.id,
    agoraSubcategoryId: item.agoraSubcategory.id,
    vendorItemId: item.vendorItemId,
    description: item.description,
    uomType: item.uomType,
    price: item.price,
    approved: item.approved,
    hasOrderCategoryOverride: item.hasOrderCategoryOverride,
    splitAmount: selectedRows.find((splitItem) => splitItem.id === item.id)?.quantity || 0,
  }));
  const formData = objectToFormData({ items: itemsToPost, orderId });
  const response = await axios.post('/SplitOrder', formData);
  return response;
}

export async function postSaveOrderAddress(orderId, address) {
  const formData = objectToFormData({ orderId, address });
  const response = await axios.post('/SaveOrderAddress', formData);
  return response;
}

export async function postSaveRequestedVendorShipDate(orderId, vendorId, requestedDate) {
  const formData = objectToFormData({ orderId, vendorId, requestedDate });
  const response = await axios.post('/SaveRequestedShipDate', formData);
  return response;
}

export async function removeRequestedVendorShipDate(orderId, vendorId) {
  const formData = objectToFormData({ orderId, vendorId });
  const response = await axios.post('/RemoveRequestedShipDate', formData);
  return response;
}

// POST
// /Api/SaveOrder
export async function postSaveOrder({ orderId, items }) {
  const itemsToPost = items?.map((item) => ({
    id: item?.unsavedId === 0 ? 0 : item?.unsavedId ? item?.unsavedId : item.id,
    itemId: item.itemId,
    vendorId: item.vendorId,
    quantity: item.quantity,
    uomAmount: item.uomAmount,
    agoraCategoryId: item.agoraCategory.id,
    agoraSubcategoryId: item.agoraSubcategory.id,
    vendorItemId: item.vendorItemId,
    description: item.description,
    uomType: item.uomType,
    price: item.price,
    approved: item.approved,
    hasOrderCategoryOverride: item.hasOrderCategoryOverride,
    removedFromBudget: item.removedFromBudget,
    substituted: item.substituted,
    replacedById: item.replacedWithVendorItemId,
  }));
  const formData = objectToFormData({ items: itemsToPost, orderId });
  const response = await axios.post('/SaveOrder', formData);
  return response;
}

// POST
// /Api/SaveOrder
export async function postSavePrevOrder({ orderId, items }) {
  const itemsToPost = items?.map((item) => ({
    id: item?.id,
    agoraCategoryId: item.agoraCategoryId,
    agoraSubcategoryId: item.agoraSubcategoryId,
    agoraCategory: item.agoraCategory,
    agoraSubcategory: item.agoraSubcategory,
  }));
  const formData = objectToFormData({ items: itemsToPost, orderId });
  const response = await axios.post('/SavePrevOrder', formData);
  return response;
}
// POST
// /Api/OrderAsPDF
export async function getOrderAsPDF({ orderId, items, orderAddress, vendorId }) {
  const itemsToPost = items?.map((item) => ({
    vendorItemId: item.vendorItemId,
    vendor: item.vendor,
    description: item.description,
    uomType: item.uomType,
    quantity: item.quantity,
    updatedQuantity: item.quantity,
    uomAmount: item.uomAmount,
    originalPrice: item.price,
    agoraSubcategory: item.agoraSubcategory?.name || item.agoraSubcategory, //Pending || Previous
    agoraSubcategoryId: item.agoraSubcategory?.id || item.agoraSubcategoryId, //Pending || Previous
  }));
  const formData = objectToFormData({ items: itemsToPost, orderId, orderAddress, vendorId });
  const response = await axios.post('/OrderAsPDF', formData, { responseType: 'blob' });
  return response;
}
// POST
// /Api/OrderAsExcel
export async function getOrderAsExcel({ orderId, items, address }) {
  const itemsToPost = items?.map((item) => ({
    vendorItemId: item.vendorItemId,
    vendor: item.vendor,
    description: item.description,
    uomType: item.uomType,
    quantity: item.originalQuantity,
    uomAmount: item.uomAmount,
    price: item.price,
    category: item.agoraCategory?.name || item.agoraCategory,
    agoraSubcategory: item.agoraSubcategory?.name || item.agoraSubcategory,
    isPPD: item.isPPD,
    agoraNumber: item.productId,
    updatedQuantity: item.quantity,
    shipping: item.budgetShipping?.map((shipping) => shipping).join(', '),
    tax: item.budgetTax?.map((tax) => tax).join(', '),
    budgetPrice: item.budgetPrice?.map((price) => price).join(', '),
    budgetQuantity: item.budgetQty?.map((quantity) => quantity).join(', '),
    statusCode: item.statusCode,
    previouslyOrdered: item.previouslyOrdered?.map((item) => item.ordered).join(', '),
    manufacturer: item.manufacturer,
    manufacturerId: item.manufacturerId,
  }));
  const formData = objectToFormData({ items: itemsToPost, orderId, address });
  const response = await axios.post('/OrderAsExcel', formData, { responseType: 'blob' });
  return response;
}

export async function getOrderOccupantsData(orderId) {
  const response = await axios.get(`/OrderOccupants?orderId=${orderId}`);
  return response;
}

export async function postLeaveOrder(orderId) {
  const response = await axios.post(`/LeaveOrder?orderId=${orderId}`);
  return response;
}
export async function getPendingOrdersData() {
  const response = await axios.get('/PendingOrders');
  return response;
}

export async function postUpdateOrderVisits(orderId, markAsRead) {
  const response = await axios.post(`/UpdateOrderVisits?orderId=${orderId}&markAsRead=${markAsRead}`);
  return response;
}

export async function ordersAdvancedSearch(input) {
  const formData = objectToFormData({ input });
  const response = await axios.post('/OrdersAdvancedSearch', formData);
  return response;
}

export async function getPreviousOrdersData({ startDate, endDate }) {
  const response = await axios.get(`/PreviousOrders?Start=${startDate}&End=${endDate}`);
  if (response.data) {
    response.data = response.data.orders?.map((order) => {
      order.facility = response.data.facilities[order.facilityId]?.name;
      order.group = response.data.facilities[order.facilityId]?.group;
      order.department =
        response.data.departments[order.departmentId] ??
        (order.departmentId === 0 ? 'No Category' : 'Deleted Category');
      const orderVendors =
        response.data.ordersVendors[order.id]?.map((v) => ({
          vendorId: v.vendorId,
          vendorName: response.data.vendors[v.vendorId],
          isMainVendor: v.isMainVendor,
          hasActiveItems: v.hasActiveItems,
        })) ?? [];
      order.mainVendorInfo = orderVendors.find((vendor) => vendor.isMainVendor) ?? orderVendors[0];
      order.subvendorsInfo = orderVendors.filter((vendor) => vendor !== order.mainVendorInfo);
      order.allvendors = orderVendors.map((vendor) => vendor.vendorName);
      if (order.deleted) return { ...order, ...order.deleted, deleteFlag: true, deleted: undefined };
      return order;
    });
  }
  return response;
}

export async function reorderCompleteOrder(orderId) {
  const response = await axios.post(`/CreateCart?orderId=${orderId}`);
  return response;
}

export async function getPreviousVendorOrdersData({ startDate, endDate }) {
  const response = await axios.get(`/PreviousVendorOrders?Start=${startDate}&End=${endDate}`);

  if (response.data) {
    response.data.vendorOrders = response.data.vendorOrders ?? [];
  }
  return response;
}

// /Api/AddToOrderList
export async function getAddToOrderListData(facilityId, categoryId) {
  const response = await axios.get(`/AddToOrderList?facilityId=${facilityId}&categoryId=${categoryId}`);
  return response;
}

export async function getPreviousOrderInfoData(orderId, vendorId = null) {
  const params = { orderId };
  const normalizedVendorId = typeof vendorId === 'string' ? vendorId.trim() : vendorId;
  const isValidVendorId = normalizedVendorId !== null && Number.isFinite(Number(normalizedVendorId));

  if (isValidVendorId) {
    params.vendorId = Number(normalizedVendorId);
  }

  const response = await axios.get('/PreviousOrder', { params });
  return response;
}

export async function getPreviousOrderInfoDataByNumber(orderId) {
  const response = await axios.get(`/PreviousOrder?orderNumber=${orderId}`);
  return response;
}
export async function getPendingOrderInfoData(orderId, shortCircuitIfApproved) {
  const response = await axios.get(
    `/ApproveOrderData?orderId=${orderId}&shortCircuitIfApproved=${shortCircuitIfApproved}`
  );
  return response;
}
export async function removedFromBudget(ids, remove) {
  const formData = objectToFormData({ ids, remove });
  const response = await axios.post(`/RemoveFromBudget`, formData);
  return response;
}

export async function deleteCompletedOrderItems(orderId, itemQuantityIds) {
  const formData = objectToFormData({ orderId, itemQuantityIds });
  const response = await axios.post('/DeleteCompletedOrderItems', formData);
  return response;
}

///Api/CheckVendorOrderSent orderId vendorId
export async function checkVendorOrderSent(orderId, vendorIds) {
  const formData = objectToFormData({
    orderId,
    vendorIds,
  });

  const response = await axios.post(`/CheckVendorsOrderSent`, formData);
  return response;
}

export async function getBudgetsData({
  facilityId,
  includeUnapproved,
  useCurrentCensus,
  orderNumber = 0,
  categories,
  tab = 0,
  budgetDate = null,
}) {
  const response = await axios.get(
    `/Budgets?facilityId=${facilityId}&includeUnapproved=${includeUnapproved}&useCurrentCensus=${useCurrentCensus}&orderNumber=${orderNumber}&yearPart=${tab}${
      budgetDate ? `&budgetDate=${formatLocalDateAsUtcMidnight(budgetDate)}` : ``
    } `
  );
  return response;
}

export async function getBudgetItemDetailsData({
  facilityId,
  categoryId,
  includeUnapproved,
  budgetDate,
  subcategoryId = 0,
  tab = 0,
}) {
  const response = await axios.get(
    `/BudgetItemDetails?facilityId=${facilityId}&categoryId=${categoryId}&includeUnapproved=${includeUnapproved}&budgetDate=${formatLocalDateAsUtcMidnight(
      budgetDate
    )}&subcategoryId=${subcategoryId}&yearPart=${tab}`
  );
  return response;
}

// /Api/DeleteOrder id
export async function deleteOrder(id, reason = null) {
  const response = await axios.post(`/deleteOrder?id=${id}&reason=${reason}`);
  return response;
}

// /Api/HardDeleteOrder id
export async function hardDeleteOrder(id) {
  const response = await axios.post(`/hardDeleteOrder?id=${id}`);
  return response;
}

// /Api/DeleteOrders ids (Array)
export async function deleteOrders(ids, reason = null) {
  const formData = objectToFormData({
    ids,
    reason,
  });
  const response = await axios.post('/DeleteOrders', formData);
  return response;
}

export async function changeDate(id, orderDate) {
  const response = await axios.post(`/ChangeOrderDelayDate?orderId=${id}&orderDate=${orderDate}`);
  return response;
}

export async function removeOrderDelay(id) {
  const response = await axios.post(`/RemoveOrderDelay?orderId=${id}`);
  return response;
}

export async function getVendorItemsForReplacement({ vendorId, facilityId, uoms }) {
  const formData = objectToFormData({ vendorId, facilityId, uoms });
  const response = await axios.post('/VendorItemsForReplacement', formData);
  return response;
}

export async function setItemPPD({ facilityId, itemId, isPPD }) {
  const formData = objectToFormData({ input: [{ itemId, isPPD }], facilityId });
  const response = await axios.post('/SetItemsPPD', formData);
  return response;
}

export async function postOrderProductForm({ parentData, productData, categories, vendorId, vendorValue }) {
  const { description, notes, files } = parentData;

  const { agoraCategoryId, agoraSubcategoryId } = categories[0];

  const productCategories =
    categories.length > 1
      ? categories?.slice(1).map((category) => ({
          agoraCategoryId: category.agoraCategoryId,
          agoraSubcategoryId: category.agoraSubcategoryId,
        }))
      : [];
  const formattedProductData = {
    VendorItemId: vendorId,
    Price: productData.price,
    UOMAmount: productData.uomAmount,
    UOMType: productData.uom,
    VendorId: vendorValue,
    Vendor: productData.vendor,
    BaseUOMAmount: productData.baseUOMAmount,
    BaseMinUOMAmount: productData.baseMinUOMAmount,
    BaseUOMType: productData.baseUOMType,
    Manufacturer: productData.manufacturer,
    ManufacturerId: productData.manufacturerId,
    IsPPD: productData.isPPD,
    Pictures: productData.pictureUrls,
    Splittable: !!productData.uomAmount2,
    VendorItemLink: productData.vendorItemLink,
    productDetailUOM: productData.uomAmount2
      ? {
          Price: productData.price2,
          UOMType: productData.uomType2,
          UOMAmount: productData.uomAmount2,
        }
      : {},
  };

  const submitData = {
    Id: 0,
    Description: description,
    Notes: notes,
    Files: files,
    AgoraCategoryId: agoraCategoryId,
    AgoraSubcategoryId: agoraSubcategoryId,
    ProductCategories: productCategories,
    ProductDetail: formattedProductData,
  };

  const response = await axios.post(`/OrderProductForm`, objectToFormData(submitData));
  return response;
}

export async function getConnectedItems(productId, orderId) {
  const response = await axios.get(`/ConnectedItems?productId=${productId}&orderId=${orderId}`);
  return response;
}

// GET / Api / OrdersSent; orderId
export async function getOrdersSent(orderId) {
  const response = await axios.get(`/OrdersSent?orderId=${orderId}`);
  return response;
}

///Api/OrderWasApprovedid
export async function getOrderWasApproved(id) {
  const response = await axios.get(`/OrderWasApproved?id=${id}`);
  return response;
}

export async function changeBudgetDate(id, budgetDate) {
  const response = await axios.post(`/ChangeBudgetDate?id=${id}&budgetDate=${budgetDate}`);
  return response;
}

export async function saveManualBudgetItemData(itemQuantityId, unitPrice, shipping, tax, qty) {
  const response = await axios.post(
    `/AddManualBudgetItem?itemQuantityId=${itemQuantityId}&unitPrice=${unitPrice}&shipping=${shipping}&tax=${tax}&qty=${qty}`
  );
  return response;
}

// GET / Api / ReviewOrder; orderId
export async function setReviewOrder(orderId) {
  const response = await axios.post(`/ReviewOrder?orderId=${orderId}`);
  return response;
}

export async function getStateAlerts(zip) {
  const severityLevels = ['Severe', 'Emergency', 'Extreme'];
  const EVENT_TYPE_ICONS = {
    Tornado: 'emojione-monotone:tornado',
    'Severe Thunderstorm': 'tdesign:thunderstorm',
    'Flash Flood': 'carbon:flood-warning',
    Flood: 'material-symbols:flood-outline',
    Hurricane: 'meteocons:hurricane',
    Typhoon: 'game-icons:entangled-typhoon',
    'Storm Surge': 'wi:flood',
    'Dust Storm': 'fluent:weather-duststorm-20-filled',
    'Snow Squall': 'mingcute:snowstorm-line',
     Earthquake: 'ri:earthquake-line',
    'Fire Weather': 'mdi:fire-alert',
    'Extreme Heat': 'mdi:heat-alert',
    'Hazardous Materials': 'healthicons:hazardous-outline',
    'Civil Emergency': 'material-symbols:emergency-home-outline',
    'Avalanche Warning': 'mdi:avalanche-outline',
    'Coastal Flood': 'fa6-solid:house-flood-water-circle-arrow-right',
    'Winter Storm Warning': 'carbon:winter-warning',
    'Severe Weather Statement': 'carbon:thunderstorm-severe',
    'Tsunami Warning': 'material-symbols:tsunami-outline',
     Wind: 'solar:wind-bold',
  };
  try {
    const responseZip = await baseAxios.get(`https://api.zippopotam.us/us/${zip}`);
    const placeData = responseZip.data.places[0];
    const latitudeLongitude = `${placeData.latitude},${placeData.longitude}`;
    const NWSAlertsApiUrl = `https://api.weather.gov/alerts/active?point=${latitudeLongitude}`;
    const response = await baseAxios.get(NWSAlertsApiUrl);

    const alertResult = response.data;

    if (alertResult.features && alertResult.features.length > 0) {
      const headlines = alertResult.features
        .filter((feature) => severityLevels.includes(feature.properties.severity))
        .map((feature) => {
          const { headline } = feature.properties;
          const eventType = Object.keys(EVENT_TYPE_ICONS).find(
            (type) => headline && headline.toLowerCase().includes(type.toLowerCase())
          );
          return {
            headline,
            icon: eventType ? EVENT_TYPE_ICONS[eventType] : null,
            message: 'Active weather alerts retrieved successfully.',
          };
        });
      if (headlines.length > 0) {
        return {
          alertCount: headlines.length,
          headlines,
          message: 'Active weather alerts retrieved successfully.',
        };
      }
    }
    const NWSForecastApiUrl = `https://api.weather.gov/points/${latitudeLongitude}`;
    const forecastPointRes = await baseAxios.get(NWSForecastApiUrl);

    const forecastUrl = forecastPointRes.data.properties.forecast;

    const forecastResponse = await baseAxios.get(forecastUrl);
    const forecastData = forecastResponse.data;

    const firstPeriod = forecastData.properties.periods?.[0];
    return {
      alertCount: 0,
      headlines: [],
      weather: {
        shortForecast: firstPeriod?.shortForecast || 'Weather data unavailable',
        icon: firstPeriod?.icon || null,
      },
      message: 'No active alerts found. Returning weather forecast.',
    };
  } catch (error) {
    console.error('Error fetching alerts:', error);
    return {
      alertCount: 0,
      headlines: [],
      message: 'Error retrieving alerts.',
    };
  }
}

export async function updateOnBehalfOf(orderId, onBehalfOfEmail) {
  const response = await axios.post(`/EditOnBehalfOf?orderId=${orderId}&onBehalfOfEmail=${onBehalfOfEmail}`);
  return response;
}

export async function getUserOrders(userEmail) {
  const response = await axios.get(`/UserOrders?userEmail=${userEmail}`);
  return response;
}

export async function getOrderLogData(orderId) {
  const response = await axios.get(`/OrderLog?orderId=${orderId}`);
  return response.data;
}
